import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Microphone, MicrophoneMute, Plus } from "iconoir-react";
import { fetchChat, postChat } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

const SUGGESTION_KEYS = [
    "add_milk",
    "whats_dinner",
    "show_shopping",
    "laundry_done",
    "show_week",
];

export default function ChatTab({ session, onRefresh }) {
    const { t, lang } = useT();
    const SUGGESTIONS = SUGGESTION_KEYS.map((k) => t(`chat.suggestion.${k}`));
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [recording, setRecording] = useState(false);
    const recogRef = useRef(null);
    const listRef = useRef(null);

    const load = async () => {
        const res = await fetchChat(session.household_id, session.member_id);
        setMessages(res);
    };
    useEffect(() => {
        load();
    }, [session.household_id, session.member_id]);

    useEffect(() => {
        listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
    }, [messages]);

    const send = async (raw) => {
        const payload = (raw ?? text).trim();
        if (!payload) return;
        setSending(true);
        setText("");
        const optimistic = {
            id: "tmp-" + Date.now(),
            role: "user",
            text: payload,
            created_at: new Date().toISOString(),
        };
        setMessages((m) => [...m, optimistic]);
        try {
            const res = await postChat(session.member_id, payload);
            setMessages((m) =>
                m.filter((x) => x.id !== optimistic.id).concat([res.user_message, res.milo_message])
            );
            onRefresh?.();
        } catch (e) {
            toast.error(t("chat.error_milo_sleeping"));
            setMessages((m) => m.filter((x) => x.id !== optimistic.id));
        } finally {
            setSending(false);
        }
    };

    const toggleMic = () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) {
            toast.error(t("chat.voice.unsupported"));
            return;
        }
        if (recording) {
            recogRef.current?.stop();
            setRecording(false);
            return;
        }
        if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
            toast.error(t("chat.voice.https"));
            return;
        }
        const rec = new SR();
        rec.lang = lang === "fr" ? "fr-FR" : "en-US";
        rec.interimResults = true;
        rec.continuous = false;
        let finalT = "";
        rec.onresult = (e) => {
            let interim = "";
            for (let i = e.resultIndex; i < e.results.length; i++) {
                const r = e.results[i];
                if (r.isFinal) finalT += r[0].transcript;
                else interim += r[0].transcript;
            }
            setText((finalT + interim).trim());
        };
        rec.onend = () => {
            setRecording(false);
            if (finalT.trim()) send(finalT.trim());
        };
        rec.onerror = (e) => {
            setRecording(false);
            const map = {
                "not-allowed": t("chat.voice.not_allowed"),
                "service-not-allowed": t("chat.voice.service_not_allowed"),
                "no-speech": t("chat.voice.no_speech"),
                "audio-capture": t("chat.voice.audio_capture"),
                "network": t("chat.voice.network"),
                "aborted": null,
            };
            const msg = map[e.error];
            if (msg) toast.error(msg);
            else if (msg !== null) toast.error(`${t("chat.voice.cant_start")} ${e.error}`);
        };
        try {
            rec.start();
            recogRef.current = rec;
            setRecording(true);
        } catch (err) {
            toast.error(`${t("chat.voice.cant_start")} ${err?.message || ""}`);
            setRecording(false);
        }
    };

    const fmtTime = (iso) => {
        try {
            return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
        } catch { return ""; }
    };

    return (
        <div data-testid="chat-tab" className="flex flex-col h-[calc(100vh-180px)] min-h-[520px]">
            <div className="mb-3">
                <div className="text-xs font-semibold text-[#5b6d63] uppercase tracking-widest">{t("chat.eyebrow")}</div>
                <div className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight mt-1">{t("chat.title")}</div>
            </div>

            <div
                ref={listRef}
                className="flex-1 brut-card p-4 sm:p-5 overflow-y-auto space-y-4"
            >
                {messages.length === 0 && (
                    <div className="h-full flex flex-col items-center justify-center text-center gap-3 py-10">
                        <div className="text-sm text-[#5b6d63]">{t("chat.suggestions_intro")}</div>
                        <div className="flex flex-wrap gap-2 justify-center">
                            {SUGGESTIONS.map((s) => (
                                <button
                                    key={s}
                                    data-testid={`suggest-${s.replace(/\s+/g, "-").toLowerCase()}-btn`}
                                    onClick={() => send(s)}
                                    className="chip chip-sage hover:bg-[#d4e5cc] transition-colors cursor-pointer"
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <AnimatePresence initial={false}>
                    {messages.map((m) => (
                        <motion.div
                            key={m.id}
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                        >
                            <div className={`max-w-[85%] sm:max-w-[70%] ${m.role === "user" ? "items-end" : "items-start"} flex flex-col gap-1`}>
                                {m.role === "milo" && (
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#2F6B43] ml-2">
                                        Milo
                                    </div>
                                )}
                                <div
                                    data-testid={`chat-${m.role}-msg`}
                                    className={
                                        m.role === "user"
                                            ? "bg-[#2F6B43] text-white rounded-[20px] rounded-br-[6px] px-4 py-2.5 text-sm whitespace-pre-wrap"
                                            : "bg-[#e8f1e4] text-[#0d2a1b] rounded-[20px] rounded-bl-[6px] px-4 py-2.5 text-sm whitespace-pre-wrap"
                                    }
                                >
                                    {m.text}
                                </div>
                                <div className={`text-[10px] text-[#5b6d63] ${m.role === "user" ? "mr-2" : "ml-2"}`}>
                                    {fmtTime(m.created_at)}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
                {sending && (
                    <div className="flex justify-start">
                        <div className="bg-[#e8f1e4] rounded-[20px] rounded-bl-[6px] px-4 py-3">
                            <div className="flex gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#2F6B43] animate-bounce" />
                                <span className="w-1.5 h-1.5 rounded-full bg-[#2F6B43] animate-bounce" style={{ animationDelay: "120ms" }} />
                                <span className="w-1.5 h-1.5 rounded-full bg-[#2F6B43] animate-bounce" style={{ animationDelay: "240ms" }} />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    send();
                }}
                className="mt-3 flex items-center gap-2 bg-white rounded-full brut-border shadow-[0_1px_2px_rgba(13,42,27,0.03),0_6px_20px_rgba(13,42,27,0.05)] p-1.5"
            >
                <button
                    type="button"
                    className="w-9 h-9 rounded-full hover:bg-[#faf5ea] flex items-center justify-center text-[#5b6d63]"
                    aria-label="more"
                >
                    <Plus className="w-4 h-4" />
                </button>
                <input
                    data-testid="chat-input"
                    className="flex-1 bg-transparent outline-none text-sm px-1 min-w-0"
                    placeholder={t("chat.placeholder")}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    disabled={sending}
                />
                {text.trim() ? (
                    <button
                        data-testid="chat-send-btn"
                        type="submit"
                        disabled={sending}
                        className="w-9 h-9 rounded-full bg-[#2F6B43] text-white flex items-center justify-center hover:bg-[#255636] disabled:opacity-50"
                    >
                        <Send className="w-4 h-4" />
                    </button>
                ) : (
                    <button
                        type="button"
                        data-testid="chat-mic-btn"
                        onClick={toggleMic}
                        className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${recording ? "bg-[#F3D469] voice-pulse-ring" : "bg-[#2F6B43] text-white hover:bg-[#255636]"}`}
                        aria-label="mic"
                    >
                        {recording ? <MicrophoneMute className="w-4 h-4 text-[#0d2a1b]" /> : <Microphone className="w-4 h-4" />}
                    </button>
                )}
            </form>
        </div>
    );
}
